import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { apiClient } from '../services/apiClient';
import { showToast } from '../store/toaster/action';
import {
  CreateCommentsType,
  DeleteCommentsType,
  DeleteNoteType,
  UpdateCommentsType,
} from '../helpers/types';

export interface NoteValueItem {
  id: number;
  name: string;
  description?: string;
  deleted_at?: string | null;
  // Definition-level label for the optional signal value. The API returns
  // `value_label` today (always null — unmapped column) and is switching to
  // `signal_value_label` to match the DB column (ENG-733). Consumers read
  // both so the backend rename needs no frontend deploy.
  value_label?: string | null;
  signal_value_label?: string | null;
}

export interface NoteCategory {
  name: string;
  note_values: NoteValueItem[];
}

export interface ThreatNoteItem {
  note_id: number;
  domain_id?: number;
  threat_id?: number;
  created_at?: string;
  created_by?: string;
  auth0_user_id?: string;
  // Signals track: the create endpoint accepts `signal_value` (the DB
  // column name), but GET echoes it back as `value` (verified live
  // 2026-07-10). notes-service is aligning GETs on `signal_value` (ENG-733)
  // — read both so the rename needs no frontend deploy.
  signal_value?: string;
  value?: string;
}

export interface ThreatNoteIdsResponse {
  items: ThreatNoteItem[];
  total?: number;
}

export interface CommentAuthorCompany {
  id: number;
  name: string;
}

export interface ThreatCommentItem {
  id: number;
  comments: string;
  is_public: boolean;
  domain_id?: number;
  threat_id?: number;
  created_at?: string;
  // Raw auth0 id for customer authors - display `author_name` when present.
  created_by?: string;
  // ENG-1031 (WHEEL-406): resolved author email and company. Not returned
  // yet - consumers fall back to `created_by` / `is_public` until it ships.
  author_name?: string | null;
  author_company?: CommentAuthorCompany | null;
}

export interface ThreatCommentsResponse {
  items: ThreatCommentItem[];
  total?: number;
}

export interface CreateNoteItem {
  domain_id: number;
  threat_id: number;
  note_id: number;
  // Signals track (WHEEL-327/330): optional concrete evidence for the reason
  // - redirect target, IP, kit name, age... Passed through to the API
  // untouched; GET endpoints echo it back as `value` until ENG-733 aligns
  // them on `signal_value`.
  signal_value?: string;
}

interface ThreatNoteIdsParams {
  domain_id?: number;
  threat_id?: number;
  page?: number;
  size?: number;
}

interface ThreatCommentsParams {
  domain_id?: number;
  threat_id?: number;
}

// Resolves the analyst-facing message from the same fallback chain the
// noteComments sagas used: axios error message, then API detail, then a
// caller-supplied default.
const getErrorMessage = (error: any, fallback: string): string =>
  error?.message ||
  error?.response?.data?.detail?.[0]?.msg ||
  error?.response?.data?.detail ||
  fallback;

export const getNotesValuesApi = async () => {
  const config = { method: 'GET', url: 'v2/evidences/notes/values' };
  return apiClient(config);
};

export const getNoteIdApi = async (payload: ThreatNoteIdsParams) => {
  const config = {
    method: 'GET',
    url: `v2/assets/${payload.domain_id}/threats/${payload.threat_id}/notes?page=${payload.page}&size=${payload.size}`,
  };
  return apiClient(config);
};

export const createNotesApi = async (data: { items: CreateNoteItem[] }) => {
  const config = { method: 'POST', url: 'v2/evidences/notes', data };
  return apiClient(config);
};

export const deleteNoteApi = async (payload: DeleteNoteType) => {
  const config = {
    method: 'DELETE',
    url: `v2/evidences/domains/${payload.domain_id}/threats/${payload.threat_id}/notes/${payload.note_id}`,
  };
  return apiClient(config);
};

export const getCommentsApi = async (payload: ThreatCommentsParams) => {
  const config = {
    method: 'GET',
    url: `v2/evidences/domains/${payload.domain_id}/threats/${payload.threat_id}/comments`,
  };
  return apiClient(config);
};

export const createCommentsApi = async (data: CreateCommentsType) => {
  const config = { method: 'POST', url: 'v2/evidences/comments', data };
  return apiClient(config);
};

export const updateCommentsApi = async (data: {
  comments: string;
  comment_id: number;
  is_public: boolean;
}) => {
  const config = {
    method: 'PUT',
    url: `v2/evidences/comments/${data.comment_id}`,
    data,
  };
  return apiClient(config);
};

export const deleteCommentsApi = async (payload: DeleteCommentsType) => {
  const config = {
    method: 'DELETE',
    url: `v2/evidences/comments/${payload.comment_id}`,
  };
  return apiClient(config);
};

const NOTES_VALUES_STALE_TIME = 5 * 60_000;

export const notesValuesQueryKey = ['noteComments', 'notesValues'];

/** Notes catalog (categories + note values) - app-global lookup data. */
export const useNotesValuesQuery = (enabled = true) =>
  useQuery<NoteCategory[]>({
    queryKey: notesValuesQueryKey,
    queryFn: async () => {
      const response = await getNotesValuesApi();
      return response?.data ?? [];
    },
    staleTime: NOTES_VALUES_STALE_TIME,
    enabled,
  });

export const threatNoteIdsQueryKey = (domainId?: number, threatId?: number) => [
  'noteComments',
  'threatNoteIds',
  domainId,
  threatId,
];

/** Notes applied to a single threat. */
export const useThreatNoteIdsQuery = (
  params?: ThreatNoteIdsParams,
  enabled = true,
) =>
  useQuery<ThreatNoteIdsResponse>({
    queryKey: threatNoteIdsQueryKey(params?.domain_id, params?.threat_id),
    queryFn: async () => {
      const response = await getNoteIdApi({
        domain_id: params?.domain_id,
        threat_id: params?.threat_id,
        page: params?.page ?? 1,
        size: params?.size ?? 50,
      });
      return response?.data ?? { items: [] };
    },
    enabled: enabled && !!params?.domain_id && !!params?.threat_id,
  });

export const threatCommentsQueryKey = (
  domainId?: number,
  threatId?: number,
) => ['noteComments', 'comments', domainId, threatId];

/** Comments on a single threat. */
export const useThreatCommentsQuery = (
  params?: ThreatCommentsParams,
  enabled = true,
) =>
  useQuery<ThreatCommentsResponse>({
    queryKey: threatCommentsQueryKey(params?.domain_id, params?.threat_id),
    queryFn: async () => {
      const response = await getCommentsApi(params as ThreatCommentsParams);
      return response?.data ?? { items: [] };
    },
    enabled: enabled && !!params?.domain_id && !!params?.threat_id,
  });

const NOTES_BATCH_SIZE = 100;

/**
 * Applies notes to one or more threats, batching requests at 100 items
 * (backend payload limit).
 *
 * WHEEL-310: when all items belong to a single threat, the backend can lag
 * behind the POST and serve a GET that doesn't include the just-created
 * notes yet. To avoid the analyst seeing a stale list, this refetches the
 * threat's notes and merges in any items still missing from the response
 * (matching the former createNotesSaga behaviour) instead of only
 * invalidating. For multi-threat (bulk) creations, each affected threat's
 * query is simply invalidated - no active viewer exists at that point.
 */
export const useCreateNotesMutation = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async (payload: { items: CreateNoteItem[] }) => {
      const allItems = payload?.items ?? [];
      if (!allItems.length) return { lastResponse: null, allItems };

      let lastResponse: any = null;
      for (let i = 0; i < allItems.length; i += NOTES_BATCH_SIZE) {
        const batch = allItems.slice(i, i + NOTES_BATCH_SIZE);
        lastResponse = await createNotesApi({ items: batch });
      }
      return { lastResponse, allItems };
    },
    onSuccess: async ({ lastResponse, allItems }) => {
      if (!allItems.length) return;

      dispatch(
        showToast(
          lastResponse?.data?.message || 'Notes successfully created',
          'success',
        ),
      );

      const uniqueThreats = new Set(
        allItems.map(item => `${item.domain_id}:${item.threat_id}`),
      );

      if (uniqueThreats.size === 1) {
        const { domain_id, threat_id } = allItems[0];
        const key = threatNoteIdsQueryKey(domain_id, threat_id);
        const fetchedRes = await getNoteIdApi({
          domain_id,
          threat_id,
          page: 1,
          size: 50,
        });
        const fetchedData: ThreatNoteIdsResponse = fetchedRes?.data ?? {
          items: [],
        };
        const fetchedItems = fetchedData.items ?? [];
        const fetchedNoteIds = new Set(fetchedItems.map(item => item.note_id));
        const missingItems = allItems.filter(
          item => !fetchedNoteIds.has(item.note_id),
        );
        queryClient.setQueryData<ThreatNoteIdsResponse>(key, {
          ...fetchedData,
          items: [...fetchedItems, ...missingItems],
        });
      } else {
        uniqueThreats.forEach(key => {
          const [domainIdStr, threatIdStr] = key.split(':');
          queryClient.invalidateQueries({
            queryKey: threatNoteIdsQueryKey(
              Number(domainIdStr),
              Number(threatIdStr),
            ),
          });
        });
      }
    },
    onError: (error: any) => {
      dispatch(
        showToast(
          getErrorMessage(error, 'An error occurred while creating notes'),
          'error',
        ),
      );
    },
  });
};

/** Removes a single note from a threat and refreshes that threat's notes. */
export const useDeleteNoteMutation = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async (payload: DeleteNoteType) => {
      const response = await deleteNoteApi(payload);
      return { data: response?.data, payload };
    },
    onSuccess: ({ payload }) => {
      queryClient.invalidateQueries({
        queryKey: threatNoteIdsQueryKey(payload.domain_id, payload.threat_id),
      });
    },
    onError: (error: any) => {
      dispatch(showToast(error?.message || 'Fail to delete note', 'error'));
    },
  });
};

/** Adds a comment to a threat and refreshes its comment list. */
export const useCreateCommentsMutation = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async (payload: CreateCommentsType) => {
      const response = await createCommentsApi(payload);
      return { data: response?.data, payload };
    },
    onSuccess: ({ payload }) => {
      dispatch(showToast('Comments successfully created', 'success'));
      const first = payload.items?.[0];
      if (first) {
        queryClient.invalidateQueries({
          queryKey: threatCommentsQueryKey(first.domain_id, first.threat_id),
        });
      }
    },
    // Matches the former createCommentsSaga, which dispatched two toasts in a
    // row (the toaster is single-slot, so only the second is ever seen).
    onError: (error: any) => {
      dispatch(
        showToast(
          getErrorMessage(error, 'An error occurred while creating comments'),
          'error',
        ),
      );
      dispatch(
        showToast(error?.message || 'Fail to create threat comment', 'error'),
      );
    },
  });
};

/** Edits a comment's text/visibility and refreshes the threat's comment list. */
export const useUpdateCommentsMutation = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async (payload: UpdateCommentsType) => {
      const response = await updateCommentsApi({
        comments: payload.comments,
        comment_id: payload.comment_id,
        is_public: payload.is_public,
      });
      return { data: response?.data, payload };
    },
    onSuccess: ({ payload }) => {
      queryClient.invalidateQueries({
        queryKey: threatCommentsQueryKey(payload.domain_id, payload.threat_id),
      });
    },
    // Matches the former updateCommentsSaga's double toast (see create above).
    onError: (error: any) => {
      dispatch(
        showToast(
          getErrorMessage(error, 'An error occurred while updating comments'),
          'error',
        ),
      );
      dispatch(
        showToast(error?.message || 'Fail to update threat comment', 'error'),
      );
    },
  });
};

/** Deletes a comment and refreshes the threat's comment list. */
export const useDeleteCommentsMutation = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async (payload: DeleteCommentsType) => {
      const response = await deleteCommentsApi(payload);
      return { data: response?.data, payload };
    },
    onSuccess: ({ payload }) => {
      queryClient.invalidateQueries({
        queryKey: threatCommentsQueryKey(payload.domain_id, payload.threat_id),
      });
    },
    onError: (error: any) => {
      dispatch(showToast(error?.message || 'Fail to delete comment', 'error'));
    },
  });
};
